import { useCallback, useMemo } from "react";
import { usePaywallCheckoutContext } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import type { PaymentError } from "@/react/contexts/Paywalls/usePaywallCheckout";
import type { CheckoutError } from "../CheckoutError";
import { reportCheckoutError } from "../checkoutErrorHelpers";
import { buildErrorMessage } from "../helpers";

export interface UseHandleErrorReturn {
  handleCheckoutError: (caller: string, error: CheckoutError) => void;
}

export const useHandleCheckoutError = () => {
  const {
    currentCommunity,
    currentCommunityMember,
    currentUser,
    handleError,
    loginRedirectUrl,
    paywall,
    resetPasswordRedirectUrl,
    setPaywallFailure,
    stripeElementsMode,
    stripePaymentMethodType,
  } = usePaywallCheckoutContext();

  const reportCheckoutErrorData = useMemo(
    () => ({
      communityId: currentCommunity?.id,
      communityMemberId: currentCommunityMember?.id,
      communityMemberUserId: currentUser?.id,
      paywallId: paywall?.id,
      stripeElementsMode,
      stripePaymentMethodType,
    }),
    [
      currentCommunity,
      currentCommunityMember,
      currentUser,
      paywall,
      stripeElementsMode,
      stripePaymentMethodType,
    ],
  );

  // TODO: drop the usage of legacy paywall errors
  const handleCheckoutError = useCallback(
    (error: CheckoutError): void => {
      reportCheckoutError(error, {
        ...reportCheckoutErrorData,
        caller: error.caller,
        extra: error.getExtra(),
      });

      if (error.isNewPaywallError()) {
        setPaywallFailure(error.context.failure);
        return;
      }

      let errorObject: Partial<PaymentError> = {
        message: error.message,
        disablePayButton: !error.shouldAllowToResubmit,
      };
      if (error.isLegacyPaywallError()) {
        errorObject = {
          ...errorObject,
          ...buildErrorMessage(error.context, {
            loginUrl: loginRedirectUrl,
            resetPasswordRedirectUrl,
          }),
        };
      }
      handleError(errorObject);
    },
    [
      reportCheckoutErrorData,
      handleError,
      loginRedirectUrl,
      resetPasswordRedirectUrl,
      setPaywallFailure,
    ],
  );

  return { handleCheckoutError };
};
